import { Component, Inject, Input } from '@angular/core';

import { BehaviorSubject, Observable, Subject, combineLatest, forkJoin, of } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';

import { SubjectRelay } from 'src/main/angular/app/shared/commands/subject-relay.class';

import { AccesService } from '../services/acces.service';
import { EntrepriseTLR } from '../../../entreprises/entities/entreprise-tlr.entity';
import { IntervenantTLR } from '../../../humain/intervenant/entities/intervenant-tlr.entity';
import { MandatoryMap } from 'src/main/angular/app/shared/helpers/mandatory-map.class';
import { isSame } from '../../../../shared/basic-data-management/has-id.interface';
import { IntervenantService } from '../../../humain/intervenant/services/intervenant.service';
import { LocalTime } from 'src/main/angular/app/shared/jsr310-localdates/types/local-time.type';
import { LocalDate } from 'src/main/angular/app/shared/jsr310-localdates/types/local-date.type';
import { JSR310ConversionService } from 'src/main/angular/app/shared/jsr310-localdates/services/jsr310-conversion.service';
import { EntrepriseService } from '../../../entreprises/services/entreprise.service';
import { safeForkJoin } from 'src/main/angular/app/technical-modules/rxjs-helpers/rxjs-helpers';
import { MessageDialogComponent } from 'src/main/angular/app/shared/dialogs/components/message-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { EventBusService, Events } from 'src/main/angular/app/technical-modules/events/services/event-bus.service';
import { HttpErrorResponse } from '@angular/common/http';
import { UpdateTarifNegatifAccesOrder } from '../entities/update-tarif-negatif-acces-order.entity';
import { IndicatifTelephone } from '../../../../static-types/entities/indicatif-telephone.entity';
import { StaticTypeService } from '../../../../static-types/services/static-types.service';
import { TypeDocument } from '../../../humain/intervenant/components/intervenant-status.component';

type IntervenantInput = {
	entreprise: EntrepriseTLR | null,
	intervenant: IntervenantTLR | null,
	modificationContacts: boolean,
	indicatifTelephone: IndicatifTelephone | null,
	telephone: string,
	email: string,
	displayablePdpTypes: TypeDocument[]
}

type RadioOption<T> = {
	value: T,
	label: string,
}


type MapIntervenantEntreprise = {
	[idEntreprise: number]: IntervenantTLR[],
}

// Fonction d'aide utilisation dans les fonction d'enregistrement
let notNull = <T>(value: T | null, nom: string) => {
	if (value === null || value === undefined) {
		alert('Le champ ' + nom + ' ne peut pas être vide.');
		throw new Error('Le champ ' + nom + ' ne peut pas être vide.');
	}
	return value;
}

@Component({
	selector: 'modification-tarif-negatif',
	templateUrl: 'modification-tarif-negatif.html',
	styleUrls: ['modification-tarif-negatif.css'],
})
export class ModificationTarifNegatifComponent {
	// Gestion globale de la fenetre
	displayState$ = new Subject<boolean>();
	step: number = 1;
	accesIds!: number[];	
	getEntrepriseNom = (entreprise: EntrepriseTLR | null) => {
		if (!entreprise) {
			return '';
		}
		if (!entreprise.nom) {
			return '';
		}
		return entreprise.nom;
	};
	entreprisesById!: MandatoryMap<number, EntrepriseTLR>;
	getIntervenantNom = (intervenant: IntervenantTLR | null) => {
		if (!intervenant || !this.entreprisesById || this.entreprisesById.isEmpty()) {
			return '';
		}

		let elems: (string | null)[] = [];
		elems.push(intervenant.nom);
		elems.push(intervenant.prenom);

		// Nom entreprise
		if (intervenant.entrepriseId) {
			let entreprise = this.entreprisesById.get(intervenant.entrepriseId);
			let nomEntreprise = this.getEntrepriseNom(entreprise);
			if (nomEntreprise) {
				elems.push('(' + nomEntreprise + ')');
			}
		}

		return elems.filter($ => $ != null).join(' ');
	};
	getIndicatifLabel = (it: IndicatifTelephone) => it ? (it.name + ' (' + it.code + ') ' + it.dialCode) : '';
	runningJobId: number | null = null;
	jobCompleted: boolean = false;
	
	// Informations statiques bind sur la vue
	entreprisesSelectionables: EntrepriseTLR[] = [];
	intervenantsSelectionables: IntervenantTLR[] = [];
	intervenantsSelectionablesByEntreprise: MapIntervenantEntreprise = {};
	indicatifTelephoneOptions: IndicatifTelephone[] = [];
	ouiNonOptions: RadioOption<boolean>[] = [
		{
			value: true,
			label: 'Oui',
		},
		{
			value: false,
			label: 'Non',
		},
	];
	
	// Saisies
	intervenantsInputs$ = new BehaviorSubject<IntervenantInput[]>([]);
	heureDebut$ = new BehaviorSubject<LocalTime | null>(null);
	dateDebut$ = new BehaviorSubject<LocalDate | null>(null);
	astreinte$ = new BehaviorSubject<boolean | null>(null);
	
	constructor(
		private dialog: MatDialog,

		@Inject(EventBusService) eventBusService: EventBusService,
		@Inject(JSR310ConversionService) private conversionService: JSR310ConversionService,

		@Inject(AccesService) private accesService: AccesService,
		@Inject(EntrepriseService) entrepriseService: EntrepriseService,
		@Inject(IntervenantService) private intervenantService: IntervenantService,
		@Inject(StaticTypeService) staticTypeService: StaticTypeService,
	) {
		// Chargement des indicatifs de telephone
		staticTypeService.getIndicatifTelephoneList().subscribe(
			(indicatifs) => this.indicatifTelephoneOptions = indicatifs
		)

		////////////////////////////////////
		// Chargement des donnees de base //
		////////////////////////////////////
		
		let entreprises$ = entrepriseService.getActiveEntreprisesTLRList().pipe(
			map(
				(entreprises) => entreprises.filter(
					$ => $.nom.toLocaleUpperCase().startsWith("ENGIE GREEN")
				)
			)
		);
		
		let entreprisesById$ = entreprises$.pipe(
			map(
				(entreprises) => {
					let map = new Map<number, EntrepriseTLR>();
					entreprises.forEach(
						$ => map.set($.id, $)
					);
					
					return new MandatoryMap<number, EntrepriseTLR>(map, "Entreprise");
				}
			)
		);
		
		let intervenantsEnreprises$ = entreprises$.pipe(
			mergeMap(
				(entreprises) => safeForkJoin(
					entreprises.map(
						(entreprise) => intervenantService.getActiveIntervenantsTLRByEntreprise(entreprise.id).pipe(
							map(
								(intervenants) => ({
									entrepriseId: entreprise.id,
									intervenants: intervenants,
								})
							)
						)
					)
				)
			),
		);
		
		let intervenantsByEntreprises$ = intervenantsEnreprises$.pipe(
			map(
				(data) => {
					let map: MapIntervenantEntreprise = {};
					data.forEach(
						$ => map[$.entrepriseId] = $.intervenants
					);
					
					return map;
				}
			),
		);
		
		let allIntervenants$ = intervenantsEnreprises$.pipe(
			map(
				(data) => {
					let intervenants = data.map($ => $.intervenants);

					let concatIntervenants: IntervenantTLR[] = [];
					intervenants.forEach(
						$ => concatIntervenants = concatIntervenants.concat($)
					);
					
					concatIntervenants = concatIntervenants.sort(
						(a, b) => {
							let c = a.nom.localeCompare(b.nom);
							if (c !== 0) {
								return c;
							}
							
							if (!a.prenom) {
								return -1;
							}
							if (!b.prenom) {
								return 1;
							}
							
							return a.prenom.localeCompare(b.prenom);
						}
					);
					
					return concatIntervenants;
				}
			),
		);
		
		let preloadedData$ = forkJoin({
			enteprises: entreprises$,
			entreprisesById: entreprisesById$,
			intervenantsByEntreprises: intervenantsByEntreprises$,
			allIntervenants: allIntervenants$, 
		});
		
		/////////////////////////////////////
		// Definition des valeurs derivees //
		/////////////////////////////////////

		// La date complete de debut en version JS est calculee depuis l'heure et la date de debut au format Java
		let dateTimeDebut$ = combineLatest([this.heureDebut$, this.dateDebut$]).pipe(map(
			(data) => {
				let [heureDebut, dateDebut] = data;

				if (heureDebut === null || dateDebut === null)
					return null;

				return conversionService.merge(dateDebut, heureDebut);
			}
		));
		let dateTimeDebutJS$ = dateTimeDebut$.pipe(map(
			(dateTimeDebut) => {
				return conversionService.fromNullableLocalDateTime(dateTimeDebut);
			}
		));

		// L'astreinte est une donne derivee de la date+heure de debut
		dateTimeDebutJS$.subscribe(
			(dateTimeDebutJS) => {
				let dateRef = dateTimeDebutJS ? dateTimeDebutJS : new Date();

				let jourSemaine = dateRef.getDay(); // 0 = Dimanche
				let jourOuvre = jourSemaine >= 1 && jourSemaine <= 5;
				let heure = dateRef.getHours();
				let heureOuvree = heure >= 8 && heure < 18;
				
				this.astreinte$.next(!jourOuvre || !heureOuvree);
			}
		);

		/////////////////////////////////////////////////////////
		// Reception et orquestration des demandes d'affichage //
		/////////////////////////////////////////////////////////
		
		let accesData$ = this.displayCommands$.pipe(
			mergeMap(
				accesIds => {
					// Pour ce panneau, les donnees utilisees sont reputees identiques
					// entre tous les acces modifier, il suffit donc de ne charger
					// que le premier pour initialiser la vue
				
					let acces$ = accesService.getById(accesIds[0]);
					let intervenants$ = accesService.getIntervenantsForAcces(accesIds[0]).pipe(
						mergeMap(
							(intervenants) => safeForkJoin(intervenants.map(intervenant => {
								return forkJoin({
									intervenant: of(intervenant),
									entreprise: entrepriseService.getActiveEntrepriseTLRById(intervenant.entrepriseId as number),
								});
							}))
						)
					);
					
					return forkJoin({
						accesIds: of(accesIds),
						acces: acces$,
						intervenants: intervenants$,
					});
				}
			)
		);
		
		combineLatest([accesData$, preloadedData$]).subscribe(
			([accesData, preloadedData]) => {
				// Binding initial de la vue
				this.step = 1;
				
				this.entreprisesSelectionables = preloadedData.enteprises;
				this.intervenantsSelectionablesByEntreprise = preloadedData.intervenantsByEntreprises;
				this.intervenantsSelectionables = preloadedData.allIntervenants;
				this.entreprisesById = preloadedData.entreprisesById;

				this.accesIds = accesData.accesIds;

				// Remplir avec les valeurs du tarif negatif
				let acces = accesData.acces;
				
				// Initialisation du bloc intervenants
				let intervenants = accesData.intervenants;
				let inputs = [] as IntervenantInput[];
				let chargeAccesIntervenantIndex = intervenants.findIndex($ => $.intervenant.roleIntervenant === 0);
				if (chargeAccesIntervenantIndex >= 0) {
					// Il y a un charge d'acces intervenant
					let chargeAcces = intervenants[chargeAccesIntervenantIndex];
					let input = {
						entreprise: chargeAcces.entreprise,
						intervenant: chargeAcces.intervenant as IntervenantTLR,
						modificationContacts: false,
					} as IntervenantInput;
					this.updateContactDisplay(input);
					inputs.push(input);
					intervenants.splice(chargeAccesIntervenantIndex, 1);
				}
				intervenants.forEach($ => {
					let input = {
						entreprise: $.entreprise,
						intervenant: $.intervenant as IntervenantTLR,
						modificationContacts: false,
					} as IntervenantInput;
					this.updateContactDisplay(input);
					inputs.push(input);
				});
				this.intervenantsInputs$.next(inputs);
				
				let dateTimeDebut = acces.dateDebut;
				this.dateDebut$.next([dateTimeDebut[0], dateTimeDebut[1], dateTimeDebut[2]]);
				this.heureDebut$.next([dateTimeDebut[3], dateTimeDebut[4], dateTimeDebut[5]]);

				this.runningJobId = null;
				this.jobCompleted = false;
				
				// Declenchement de l'affichage				
				this.displayState$.next(true);
			}
		)
		
		this.displayState$.pipe(
			filter(
				(display) => !display
			)
		).subscribe(
			(_display) => {
				this.runningJobId = null;
				this.jobCompleted = false;

				// Signaler l'evenement de creation au bus d'evenement general
				if (this.step === 2 ) {
					eventBusService.emit(Events.AccesListChanged, null);
				}
			}
		)
	}

	@Input() id = 'modificationTarifNegatif';
	
	// Liaison de l'observable servant a commander l'affichage du panneau
	private displayCommands$ = new SubjectRelay<number[]>();
	@Input() set displayCommands(obs: Observable<number[]> | undefined) {
		this.displayCommands$.registerSource(obs);
	}

	addIntervenantInput() {
		let inputs = this.intervenantsInputs$.getValue();
		inputs.push({
			entreprise: null,
			intervenant: null,
			modificationContacts: false,
		} as IntervenantInput);
		this.intervenantsInputs$.next(inputs);
	}

	removeIntervenantInput(index: number) {
		let inputs = this.intervenantsInputs$.getValue();
		inputs.splice(index, 1);
		this.intervenantsInputs$.next(inputs);
	}

	onEntrepriseChange(input: IntervenantInput, entreprise: EntrepriseTLR | null) {
		let inputs = this.intervenantsInputs$.getValue();
		if (isSame(input.entreprise, entreprise)) {
			return;
		}

		input.entreprise = entreprise;
		input.intervenant = null;
		this.intervenantsInputs$.next(inputs);
	}

	onIntervenantChange(input: IntervenantInput, intervenant: IntervenantTLR | null) {
		let inputs = this.intervenantsInputs$.getValue();
		if (isSame(input.intervenant, intervenant)) {
			return;
		}

		input.intervenant = intervenant;
		input.modificationContacts = false;
		input.displayablePdpTypes = []; // car intervenant interne et acces distant

		this.updateContactDisplay(input);

		// Si l'utilisateur selectionne un intervenant avant de selectionner son entreprise,
		// remplir automatiquement l'entreprise		
		if (intervenant && !input.entreprise) {
			// entrepriseId est forcement non null puisqu'on a recuperer l'intervenant a travers une entreprise
			let entrepriseId = intervenant.entrepriseId as number;
			let entreprise = this.entreprisesById.get(entrepriseId);
			input.entreprise = entreprise;
		}

		this.intervenantsInputs$.next(inputs);
	}

	private updateContactDisplay(input: IntervenantInput) {
		let intervenant = input.intervenant;

		if (intervenant) {
			let indexIT = this.indicatifTelephoneOptions.findIndex($ => $.dialCode === (intervenant as IntervenantTLR).indicatifTelephone);
			input.indicatifTelephone = this.indicatifTelephoneOptions[indexIT];
			input.telephone = this.nonNull(intervenant.telephone);
			input.email = this.nonNull(intervenant.email);
		}
	}

	onCancelModificationContact(input: IntervenantInput) {
		input.modificationContacts = false;
		this.updateContactDisplay(input);
	}
	
	private nonNull(value: string | null): string {
		return value ? value : '';
	}

	onInputModificationContact(input: IntervenantInput) {
		input.modificationContacts = false;

		if (!input.intervenant) {
			throw new Error("input.intervenant ne devrait pas etre null a ce point");
		}

		let intervenant = input.intervenant;
		let dialCode = input.indicatifTelephone ? input.indicatifTelephone.dialCode : null;

		this.intervenantService.updateContactInfos(
			intervenant.id,
			dialCode,
			input.telephone,
			input.email,
		).subscribe(
			(_) => {
				intervenant.indicatifTelephone = dialCode;
				intervenant.telephone = input.telephone;
				intervenant.email = input.email;
			}
		)
	}
	
	cancel() {
		this.displayState$.next(false);
	}
	
	finish() {
		this.displayState$.next(false);
	}

	modify() {
		let errors: string[] = [];

		let inputs = this.intervenantsInputs$.getValue();
		let entrepriseMissing = inputs.some($ => $.entreprise === null);		
		if (entrepriseMissing) {
			errors.push("Vous devez renseigner l'entreprise pour chaque ligne.");
		}

		let intervenantMissing = inputs.some($ => $.intervenant === null);		
		if (intervenantMissing) {
			errors.push("Vous devez renseigner les intervenants pour chaque ligne.");
		}
		
		let intervenantsId = inputs
			.map($ => $.intervenant)
			.filter($ => $)
			// Du au filtre precedent, la valeur -1 ne devrai jamais sortir
			.map($ => $ === null ? -1 : $.id)
			;
		let intervenantDoublon = false;
		let known = new Map<number, boolean>();
		for (let intervenantId of intervenantsId) {
			if (known.get(intervenantId)) {
				intervenantDoublon = true;
			}
			known.set(intervenantId, true);
		}
		if (intervenantDoublon) {
			errors.push("Vous ne devez pas avoir d'intervenants en double.");
		}

		let heureDebut = this.heureDebut$.getValue();
		if (heureDebut === null) {
			errors.push("Veuillez renseigner l'heure de début.");
		}

		let dateDebut = this.dateDebut$.getValue();
		if (dateDebut === null) {
			errors.push("Veuillez renseigner la date de début.");
		}
		
		if (this.displayValidationErrors(errors)) {
			return;
		}

		let intervenantsIds = inputs.filter($ => $.intervenant).map($ => notNull($.intervenant, "intervenant").id);
		let chargeAccesId = intervenantsIds[0];
		intervenantsIds.splice(0, 1);

		let debut = this.conversionService.merge(dateDebut as LocalDate, heureDebut as LocalTime);
		let astreinte = this.astreinte$.getValue();

		let order = {
			accesIds: this.accesIds,
			chargeAccesId: chargeAccesId,
			intervenantsIds: intervenantsIds,
			debut: debut,
			astreinte: astreinte,
		} as UpdateTarifNegatifAccesOrder;

		this.accesService.launchModifyTarifNegatifAccesJob(order).subscribe(
			(jobId) => {
				this.runningJobId = jobId;
				this.jobCompleted = false;
				this.step = 2;
			},
			(error: HttpErrorResponse) => {
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: "Erreur lors de la modification des accès",
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors la tentivate de modification des accès", error);
			}
		);
	}

	// Affiche les messages d'erreur de validation
	private displayValidationErrors(errors: string[]): boolean {
		if (errors.length > 0) {
			this.dialog.open(MessageDialogComponent, {
				width: '400px',
				data: {
					title: 'Enregistrement impossible',
					messages: errors
				}
			});

			return true;
		}

		return false;
	}
}